/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License 2.0 which accompany this distribution.
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *   https://www.eclipse.org/legal/epl-2.0/
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

const isCellBased = (item) => {
	const isShape = item._parent && item._parent.isCellsNode;
	return isShape && item.getItemAttributes().getCellBased().getValue() === true;
};
const getSectionAndOffset = (sheet, item) => {
	const center = item.getCenter();
	const size = item.getSizeAsPoint();
	return {
		colLeft: sheet.columns.getSectionAndOffset(center.x - size.x / 2),
		rowTop: sheet.rows.getSectionAndOffset(center.y - size.y / 2),
		colRight: sheet.columns.getSectionAndOffset(center.x + size.x / 2),
		rowBottom: sheet.rows.getSectionAndOffset(center.y + size.y / 2)
	};
};
const updateShapePosition = (sheet, item, json) => {
	if (isCellBased(item)) {
		const { colLeft, colRight, rowTop, rowBottom } = getSectionAndOffset(sheet, item);
		json.sheetPos = {
			colLeft: colLeft.index,
			colLeftOffset: colLeft.offset,
			rowTop: rowTop.index,
			rowTopOffset: rowTop.offset,
			colRight: colRight.index,
			colRightOffset: colRight.offset,
			rowBottom: rowBottom.index,
			rowBottomOffset: rowBottom.offset
		};
	}
	return json;
};
const updateShapeGeometry = (name, info, range) => (sheet, item, json) => {
	if (isCellBased(item)) {
		const { colLeft, colRight, rowTop, rowBottom } = getSectionAndOffset(sheet, item);
		let offset = 0;
		if (
			info.type === 'rows' ||
			(info.type === 'cells' &&
				(info.direction === 'up' || info.direction === 'bottom') &&
				colLeft.index >= range.getX1() &&
				colRight.index <= range.getX2())
		) {
			offset = name === 'command.server.InsertCellsCommand' ? range.height : -range.height;
			if (offset < 0 && rowTop.index >= range.getY1() && rowTop.index <= range.getY2()) {
				rowTop.index = range.getY1();
				rowTop.offset = 0;
			} else if (range.getY1() <= rowTop.index) {
				rowTop.index += offset;
			}
			if (offset < 0 && rowBottom.index >= range.getY1() && rowBottom.index <= range.getY2()) {
				rowBottom.index = range.getY1();
				rowBottom.offset = 0;
			} else if (range.getY1() <= rowBottom.index) {
				rowBottom.index += offset;
			}
		} else if (
			info.type === 'cols' ||
			(info.type === 'cells' &&
				(info.direction === 'left' || info.direction === 'right') &&
				rowTop.index >= range.getY1() &&
				rowBottom.index <= range.getY2())
		) {
			offset = name === 'command.server.InsertCellsCommand' ? range.width : -range.width;
			if (offset < 0 && colLeft.index >= range.getX1() && colLeft.index <= range.getX2()) {
				colLeft.index = range.getX1();
				colLeft.offset = 0;
			} else if (range.getX1() <= colLeft.index) {
				colLeft.index += offset;
			}
			if (offset < 0 && colRight.index >= range.getX1() && colRight.index <= range.getX2()) {
				colRight.index = range.getX1();
				colRight.offset = 0;
			} else if (range.getX1() <= colRight.index) {
				colRight.index += offset;
			}
		}
		json.sheetPos = {
			colLeft: colLeft.index,
			colLeftOffset: colLeft.offset,
			rowTop: rowTop.index,
			rowTopOffset: rowTop.offset,
			colRight: colRight.index,
			colRightOffset: colRight.offset,
			rowBottom: rowBottom.index,
			rowBottomOffset: rowBottom.offset
		};
	}
	return json;
};
const collectItems = (container, items, updater) => {
	const sheetId = container.model.properties.sheetId;
	if (!items.has(sheetId)) {
		const descriptors =
			sheetId && container.getStreamSheet().getCells().subItemsToJSON(container.model.sheet, updater);
		if (descriptors) items.set(sheetId, descriptors);
	}
};
const updateGraphItems = (graph, updater) => {
	const items = new Map();
	const container = graph.streamSheetsContainer;
	const { leftSheetContainer, rightSheetContainer } = container;
	const fstContainer = graph.activeSheetContainer === leftSheetContainer ? leftSheetContainer : rightSheetContainer;
	const sndContainer = fstContainer === leftSheetContainer ? rightSheetContainer : leftSheetContainer;
	collectItems(fstContainer, items, updater);
	collectItems(sndContainer, items, updater);
	return { streamsheetIds: Array.from(items.keys()), graphItems: Array.from(items.values()) };
};

// TODO: rename...
const updateGraphItemsGeometry = (graph, selection) => (request) => {
	const updater = request ? updateShapeGeometry(request.name, request.info, selection) : undefined;
	return updateGraphItems(graph, updater);
};

const updateGraphItemsPosition = (graph) => updateGraphItems(graph, updateShapePosition);

module.exports = {
	updateGraphItemsGeometry,
	updateGraphItemsPosition
};
